export interface SEOConfig {
  title: string;
  description: string;
  keywords: string;
  image?: string;
  url?: string;
  type?: string;
  noIndex?: boolean;
  structuredData?: object;
}

const baseUrl = 'https://pay.pesajet.com';
const defaultImage = `${baseUrl}/og-image.png`;

export const seoConfigs: Record<string, SEOConfig> = {
  home: {
    title: 'PesaJet Pay - Mobile Money Payment Gateway Uganda | MTN & Airtel Integration',
    description:
      "Accept mobile money payments from MTN and Airtel in Uganda and East Africa with PesaJet Pay's unified API and official @pesajet/sdk Node.js package. Built by PesaJet (evolved from E-Moments Tech Enterprise). Simple integration, real-time processing, and transparent 3.5% pricing.",
    keywords:
      'emoments, e-moments, E-Moments, E-Moments Tech Enterprise, Emily Sonia Nakabuye, @pesajet/sdk, PesaJet SDK, Node.js payment SDK, PesaJet Pay, payment gateway, payment gateway Uganda, online payment gateway, mobile money payment gateway, payment API, payments API integration, payment gateway integration, payment processing, online payments, digital payments, mobile payments, mobile money Uganda, mobile money API, mobile money integration, MTN Mobile Money Uganda, MTN MoMo API, Airtel Money Uganda, Airtel Money API, merchant payments, business payments, payment collection, payment disbursement, payment checkout, payment links, transaction processing, secure payment processing, real-time payments, payment automation, fintech Uganda, Uganda payments, East Africa payments, Kenya payments, Tanzania payments, Africa payment gateway, African fintech API',
    url: baseUrl,
    image: defaultImage,
  },

  login: {
    title: 'Sign In to PesaJet Pay - Mobile Money Payment Dashboard',
    description:
      'Access your PesaJet Pay merchant dashboard to manage mobile money transactions, view analytics, and configure payment settings for MTN and Airtel.',
    keywords:
      'PesaJet Pay login, merchant dashboard, mobile money management, payment analytics, MTN Airtel dashboard',
    url: `${baseUrl}/login`,
    image: defaultImage,
    noIndex: true, // Don't index login pages
  },

  signup: {
    title: 'Get Started with PesaJet Pay - Create Your Merchant Account',
    description:
      'Sign up for PesaJet Pay and start accepting mobile money payments from MTN and Airtel customers across East Africa. Free account setup with 3.5% transaction fees.',
    keywords:
      'PesaJet Pay signup, create merchant account, mobile money registration, MTN Airtel integration, payment gateway registration',
    url: `${baseUrl}/signup`,
    image: defaultImage,
  },

  terms: {
    title: 'Terms and Conditions - PesaJet Pay',
    description:
      'Read the PesaJet Pay terms and conditions for merchants, users, payments, settlements, refunds, compliance, security, and platform use.',
    keywords:
      'PesaJet Pay terms, PesaJet Pay conditions, merchant terms, payment gateway terms Uganda, settlement terms',
    url: `${baseUrl}/terms`,
    image: defaultImage,
  },

  privacy: {
    title: 'Privacy Policy - PesaJet Pay',
    description:
      'Read the PesaJet Pay Privacy Policy governing personal data collection, KYC verification, 10-year AML retention, cross-border processing, and Data Subject rights under the Uganda Data Protection and Privacy Act, 2019.',
    keywords:
      'PesaJet Pay privacy policy, Uganda Data Protection and Privacy Act 2019, PDPO compliance, merchant KYC privacy, mobile money data security',
    url: `${baseUrl}/privacy`,
    image: defaultImage,
  },

  dashboard: {
    title: 'Dashboard - PesaJet Pay Merchant Portal',
    description:
      'View your payment analytics, transaction history, and account overview in the PesaJet Pay merchant dashboard.',
    keywords:
      'merchant dashboard, payment analytics, transaction overview, mobile money statistics',
    url: `${baseUrl}/dashboard`,
    image: defaultImage,
    noIndex: true, // Private dashboard
  },

  transactions: {
    title: 'Transactions - PesaJet Pay Payment History',
    description:
      'View and manage all your mobile money transactions from MTN and Airtel customers. Track payment status, amounts, and customer details.',
    keywords:
      'transaction history, payment tracking, mobile money transactions, MTN Airtel payments',
    url: `${baseUrl}/transactions`,
    image: defaultImage,
    noIndex: true, // Private transactions
  },

  newTransaction: {
    title: 'Create New Payment - PesaJet Pay',
    description:
      'Create a new mobile money payment request for MTN or Airtel customers. Simple form to initiate payments with real-time processing.',
    keywords: 'create payment, new transaction, mobile money request, MTN Airtel payment',
    url: `${baseUrl}/transactions/new`,
    image: defaultImage,
    noIndex: true, // Private functionality
  },

  kyc: {
    title: 'KYC Verification - PesaJet Pay Account Setup',
    description:
      'Complete your Know Your Customer (KYC) verification to unlock full payment processing capabilities with PesaJet Pay.',
    keywords:
      'KYC verification, account verification, merchant verification, business verification',
    url: `${baseUrl}/kyc`,
    image: defaultImage,
    noIndex: true, // Private KYC process
  },

  apiKeys: {
    title: 'API Keys - PesaJet Pay Developer Settings',
    description:
      'Manage your API keys and integration settings for PesaJet Pay mobile money payment gateway. Secure access to MTN and Airtel APIs.',
    keywords: 'API keys, developer settings, integration keys, payment API access',
    url: `${baseUrl}/api-keys`,
    image: defaultImage,
    noIndex: true, // Private API settings
  },

  settings: {
    title: 'Account Settings - PesaJet Pay Merchant Portal',
    description:
      'Manage your PesaJet Pay account settings, notification preferences, and business information.',
    keywords: 'account settings, merchant settings, notification preferences, business profile',
    url: `${baseUrl}/settings`,
    image: defaultImage,
    noIndex: true, // Private settings
  },

  documentation: {
    title: 'API & SDK Documentation - PesaJet Pay Developer Guide (@pesajet/sdk)',
    description:
      'Complete API and SDK documentation for integrating PesaJet Pay mobile money payments. Official Node.js SDK (@pesajet/sdk on npm), code examples, endpoints, and webhook verification for MTN and Airtel.',
    keywords:
      'emoments, e-moments, E-Moments, @pesajet/sdk, pesajet sdk, pesajet node sdk, pesajet npm, npm @pesajet/sdk, API documentation, developer guide, integration guide, mobile money API, MTN API, Airtel API, payment integration, typescript payment sdk',
    url: `${baseUrl}/docs`,
    image: defaultImage,
  },

  verifyEmail: {
    title: 'Verify Email - PesaJet Pay Account Activation',
    description:
      'Verify your email address to activate your PesaJet Pay merchant account and start accepting mobile money payments.',
    keywords: 'email verification, account activation, merchant verification',
    url: `${baseUrl}/verify-email`,
    image: defaultImage,
    noIndex: true, // Don't index verification pages
  },
};

export function getSEOConfig(page: string): SEOConfig {
  return seoConfigs[page] || seoConfigs.home;
}

// Structured data generators
export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'PesaJet Pay',
    url: baseUrl,
    logo: `${baseUrl}/logo.png`,
    description:
      'Mobile money payment gateway for Uganda and East Africa supporting MTN and Airtel payments',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+256787611629',
      contactType: 'customer service',
      email: 'support@pesajet.com',
      availableLanguage: ['English'],
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'UG',
      addressRegion: 'Central Region',
      addressLocality: 'Kampala',
      addressStreet: 'Kanjokya House, Level 4, Kanjokya Street, Nakasero',
    },
    sameAs: ['https://twitter.com/pesajet', 'https://linkedin.com/pesajet'],
  };
}

export function generateServiceSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Mobile Money Payment Gateway',
    description:
      'Unified API for accepting MTN and Airtel mobile money payments in Uganda and across East Africa',
    provider: {
      '@type': 'Organization',
      name: 'PesaJet Pay',
    },
    areaServed: [
      {
        '@type': 'Country',
        name: 'Uganda',
      },
      {
        '@type': 'Country',
        name: 'Kenya',
      },
      {
        '@type': 'Country',
        name: 'Tanzania',
      },
    ],
    serviceType: 'Payment Processing',
    offers: {
      '@type': 'Offer',
      description: 'Pay-as-you-go pricing at 2.5% per transaction',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '2.5',
        priceCurrency: 'USD',
        unitText: 'PERCENT',
      },
    },
  };
}

export function generateWebPageSchema(page: string) {
  const config = getSEOConfig(page);
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: config.title,
    description: config.description,
    url: config.url || baseUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: 'PesaJet Pay',
      url: baseUrl,
    },
  };
}
